import React, { useState, useRef } from 'react';
import { Sidebar, MobileMenuButton } from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { Bell, HelpCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { notificationsAPI } from '../../lib/api';
import { useEffect } from 'react';
import { formatDateTime } from '../../lib/utils';
import { playNotificationSound, unlockAudio } from '../../lib/sound';

export const Layout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showPushBanner, setShowPushBanner] = useState(false);
  const { user } = useAuth();
  const { startTutorial } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const { permission, subscribed, requesting, supported, requestPermission } = usePushNotifications();
  const knownNotifIds = useRef(null);

  useEffect(() => {
    unlockAudio();
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  // Mostra banner attivazione push dopo 5s se non già concesso e non negato
  useEffect(() => {
    if (!supported) return;
    if (permission === 'default' && !subscribed) {
      const t = setTimeout(() => setShowPushBanner(true), 5000);
      return () => clearTimeout(t);
    }
  }, [supported, permission, subscribed]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      const data = response.data;
      if (knownNotifIds.current === null) {
        // Prima chiamata: inizializza senza suonare
        knownNotifIds.current = new Set(data.map(n => n.id));
      } else {
        const newUnread = data.filter(n => !n.letta && !knownNotifIds.current.has(n.id));
        if (newUnread.length > 0) {
          playNotificationSound();
          newUnread.forEach(n => knownNotifIds.current.add(n.id));
        }
        // Aggiunge anche quelle già lette per non risuonarle se diventano unread
        data.forEach(n => knownNotifIds.current.add(n.id));
      }
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.letta).length;

  const markAsRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, letta: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, letta: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-4">
              <MobileMenuButton onClick={() => setSidebarOpen(true)} />
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                {title}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Tutorial help button */}
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-indigo-600"
                onClick={() => startTutorial(location.pathname)}
                title="Mostra guida per questa pagina"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    data-testid="notifications-btn"
                  >
                    <Bell className="h-5 w-5 text-slate-600" />
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="font-medium text-sm">Notifiche</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-indigo-600"
                        onClick={markAllAsRead}
                      >
                        Segna tutte come lette
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-500">
                        Nessuna notifica
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notification) => (
                        <DropdownMenuItem
                          key={notification.id}
                          className="flex flex-col items-start gap-1 px-4 py-3 cursor-pointer"
                          onClick={() => {
                            markAsRead(notification.id);
                            if (notification.link) navigate(notification.link);
                          }}
                        >
                          <div className="flex items-start gap-2 w-full">
                            {!notification.letta && (
                              <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {notification.titolo}
                              </p>
                              <p className="text-xs text-slate-500 line-clamp-2">
                                {notification.messaggio}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDateTime(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User avatar */}
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-xs font-semibold text-indigo-700">
                    {user?.nome?.[0]}{user?.cognome?.[0]}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Banner attivazione notifiche push */}
        {showPushBanner && permission === 'default' && (
          <div className="mx-4 sm:mx-6 mt-4 flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
            <Bell className="h-5 w-5 text-indigo-600 shrink-0" />
            <p className="flex-1 text-sm text-indigo-800">
              Attiva le <strong>notifiche push</strong> per ricevere avvisi anche quando l'app è in background.
            </p>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
              disabled={requesting}
              onClick={async () => { await requestPermission(); setShowPushBanner(false); }}>
              {requesting ? 'Attivazione…' : 'Attiva'}
            </Button>
            <button onClick={() => setShowPushBanner(false)}
              className="text-indigo-400 hover:text-indigo-600 shrink-0 text-lg leading-none">×</button>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto animate-fadeIn">{children}</div>
        </main>
      </div>
    </div>
  );
};
